import { getDataPointId } from '@modules/common/utils';
import { QuantifyReportDataPoint } from '@modules/quantify/store/types';
import { AggregateBucketKey, AggregateDataV2 } from 'generated/graphql';
import { round } from 'lodash';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getQuantifyAggregateFormulaResponseParams {
  aggregateDataV2List: AggregateDataV2[];
}

export function getQuantifyAggregateFormulaResponse({
  aggregateDataV2List,
}: getQuantifyAggregateFormulaResponseParams): QuantifyReportDataPoint {
  const dataPoints = aggregateDataV2List
    ?.map(({ bucket, values }) => {
      if (bucket == null) {
        return undefined;
      }

      const aggregateBucket = bucket as AggregateBucketKey;

      const id = getDataPointId(aggregateBucket);
      const isInvisible = aggregateBucket.label?.isVisible === false;
      const isUpdateInProgress =
        Boolean(aggregateBucket.label?.updateInProgress) && !isInvisible;

      const variants: QuantifyReportDataPoint['variants'] = {};

      values.forEach((value) => {
        const variant = value.timestamp;

        const nps = value.intermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.NPS,
        )?.value.nps;

        const csat = value.intermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.CSAT,
        )?.value.csat;

        const mean = value.intermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.Mean,
        )?.value.mean;

        if (!value.queryID && variant) {
          variants[variant] = {
            id: String(variant),
            label: String(value.timestamp),
            value: round(value.value, 2),
            nps,
            csat,
            mean,
          };
        }
      });

      const accValue = values.find(({ timestamp }) => {
        return !timestamp;
      });

      const nps = accValue?.intermediates?.find(
        (intermediate) => intermediate.func === EQLFunctionName.NPS,
      )?.value.nps;

      const csat = accValue?.intermediates?.find(
        (intermediate) => intermediate.func === EQLFunctionName.CSAT,
      )?.value.csat;

      const mean = accValue?.intermediates?.find(
        (intermediate) => intermediate.func === EQLFunctionName.Mean,
      )?.value.mean;

      const dataPoint: QuantifyReportDataPoint = {
        id,
        label: '',
        value: round(accValue?.value ?? 0, 2),
        variants,
        timestamp: undefined,
        entitySentiment: undefined,
        anomalyValue: undefined,
        isAnomalous: false,
        isRelevant: false,
        category: undefined,
        nps,
        csat,
        mean,
        isInvisible,
        isUpdateInProgress,
      };

      return dataPoint;
    })
    .filter((item): item is QuantifyReportDataPoint => !!item);

  return dataPoints[0];
}
